import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Skull, X } from "lucide-react";
import { AttackRowsEditor } from "./AttackRowsEditor";

const EMPTY_FORM = { name: "", level: 1, ac: 15, hp_max: 10, speed: 25, fort: 0, ref: 0, will: 0, perception: 0, attacks: [], abilities: "" };

export default function MonsterBuilderPanel({ game, onSpawnToken }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [conditions, setConditions] = useState([]);

  const load = () => api.get(`/games/${game.id}/monster-templates`).then((r) => setTemplates(r.data));
  useEffect(() => {
    load();
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
  }, [game.id]);

  const setField = (key, value) => setForm({ ...form, [key]: value });

  const createTemplate = async () => {
    if (!form.name.trim()) return toast.error("Give the monster a name");
    await api.post(`/games/${game.id}/monster-templates`, form);
    setForm(EMPTY_FORM);
    load();
    toast.success("Monster template saved");
  };

  const deleteTemplate = async (id) => {
    await api.delete(`/games/${game.id}/monster-templates/${id}`);
    load();
  };

  const spawnFromTemplate = (t) => {
    onSpawnToken({
      name: t.name, image_url: t.image_url, token_type: "npc", hp_current: t.hp_max, hp_max: t.hp_max,
      stat_block: { ac: t.ac, speed: t.speed, fort: t.fort, ref: t.ref, will: t.will, perception: t.perception, attacks: t.attacks, abilities: t.abilities },
      perception_dc: 10 + (t.perception || 0),
    });
    toast.success(`${t.name} placed on the map`);
  };

  return (
    <div className="border-t border-white/10 pt-3 space-y-4">
      <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Skull className="w-3 h-3" /> Monster Library</p>
      <p className="text-[10px] text-[#f1e0c5]/30">Spawn a starter monster straight onto the map, or build and save your own below.</p>

      <div className="space-y-1.5">
        <Input data-testid="monster-name-input" placeholder="Monster name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="bg-black/30 border-white/10 h-8 text-xs" />
        <div className="grid grid-cols-3 gap-1.5">
          <Input data-testid="monster-level-input" type="number" placeholder="Level" value={form.level} onChange={(e) => setField("level", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="monster-ac-input" type="number" placeholder="AC" value={form.ac} onChange={(e) => setField("ac", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="monster-hp-input" type="number" placeholder="HP" value={form.hp_max} onChange={(e) => setField("hp_max", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          <Input data-testid="monster-speed-input" type="number" placeholder="Speed" value={form.speed} onChange={(e) => setField("speed", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="monster-fort-input" type="number" placeholder="Fort" value={form.fort} onChange={(e) => setField("fort", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="monster-ref-input" type="number" placeholder="Ref" value={form.ref} onChange={(e) => setField("ref", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="monster-will-input" type="number" placeholder="Will" value={form.will} onChange={(e) => setField("will", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs" />
        </div>
        <Input data-testid="monster-perception-input" type="number" placeholder="Perception" value={form.perception} onChange={(e) => setField("perception", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs w-24" />

        <p className="text-[10px] uppercase text-[#f1e0c5]/40 pt-1">Attacks</p>
        <AttackRowsEditor attacks={form.attacks} onChange={(attacks) => setForm({ ...form, attacks })} conditions={conditions} testIdPrefix="monster" />

        <Textarea data-testid="monster-abilities-input" placeholder="Special abilities / notes" value={form.abilities} onChange={(e) => setField("abilities", e.target.value)} className="bg-black/30 border-white/10 text-xs min-h-16" />
        <Button data-testid="monster-create-button" onClick={createTemplate} size="sm" className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">Save Monster</Button>
      </div>

      <div className="space-y-1.5">
        {templates.map((t) => (
          <div key={t.id} data-testid={`monster-template-${t.id}`} className="flex items-center justify-between bg-black/20 rounded-lg px-2.5 py-2">
            <div>
              <p className="text-sm">
                {t.name} <span className="text-[10px] text-[#f1e0c5]/30">Lv{t.level} · AC {t.ac} · HP {t.hp_max}</span>
                {!t.game_id && <span className="ml-1.5 text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">Starter</span>}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button data-testid={`spawn-monster-${t.id}`} onClick={() => spawnFromTemplate(t)} size="sm" variant="secondary" className="rounded-full text-xs h-7">Spawn</Button>
              {t.game_id && (
                <button data-testid={`delete-monster-${t.id}`} onClick={() => deleteTemplate(t.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No saved monsters yet.</p>}
      </div>
    </div>
  );
}
